/** @format */

import { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import { breakpointFrom, breakpointUpTo } from '../../theme/mixins';
import { ContainerLeftSide, ContainerRightSide } from '../ui/Layout';
import { H3, H4, P } from '../ui/Typography';
import { ProjectCard } from '@/types/shared';
import { RichText } from '../ui/RichText';
import { SliderExternalControls } from '../Slider';
import ChevronLeft from '../../assets/icons/arrow_left.svg';
import ChevronRight from '../../assets/icons/arrow_right.svg';
import { Icon } from '../ui/Icon';
import { StyledButton } from '../ui/Button';
import { i18nContext } from '@/context/i18nContext';

export const Project = ({ project }: Props) => {
  const swiperRef = useRef<any>(null);
  const [isFirstSlide, setIsFirstSlide] = useState(true);
  const [isLastSlide, setIsLastSlide] = useState(false);
  const [activeSlide, setActiveSlide] = useState(1);
  const [currentClassName, setCurrentClassName] = useState('forward');

  const handlePrevious = useCallback(() => {
    swiperRef.current?.swiper.slidePrev();
  }, [swiperRef]);

  const handleNext = useCallback(() => {
    swiperRef.current?.swiper.slideNext();
  }, [swiperRef]);

  const { city, location, description, productType, target, surface, rooms, year, gallery, videoURL } = project;
  return (
    <i18nContext.Consumer>
      {t => (
        <ProjectContainer className="reverse">
          <InformationsWrapper>
            <TitleWrapper>
              <H3>{productType}</H3>
              <P>
                {city} - {location}
              </P>
            </TitleWrapper>
            <DescriptionWrapper>
              <RichText text={description} />
            </DescriptionWrapper>
            <FeatureList>
              <div>
                <div>
                  <span className="title">Target</span> <Chip as="span">{target}</Chip>
                </div>
                <div>
                  <span className="title">Units</span> <Chip as="span">{rooms}</Chip>
                </div>
              </div>
              <div>
                <div>
                  <span className="title">Superficie</span> <Chip as="span">{surface}</Chip>
                </div>
                <div>
                  <span className="title">Year</span> <Chip as="span">{year}</Chip>
                </div>
              </div>
            </FeatureList>
            <VideoURLContainer>
              {videoURL && (
                <StyledButton
                  item={videoURL}
                  position={videoURL.fields.position ?? 'right'}
                  variant={videoURL.fields.variant ?? 'bgTeal'}
                  elementType="magic"
                >
                  {videoURL.fields.label}
                </StyledButton>
              )}
            </VideoURLContainer>
          </InformationsWrapper>
          <GalleryWrapper>
            <ImageWrapper>
              <SliderExternalControls
                classNameState={[currentClassName, setCurrentClassName]}
                images={gallery}
                ref={swiperRef}
                isFirstSlideState={[isFirstSlide, setIsFirstSlide]}
                isLastSlideState={[isLastSlide, setIsLastSlide]}
                activeSlideState={[activeSlide, setActiveSlide]}
              />
            </ImageWrapper>
            <Controls>
              <button onClick={handlePrevious} title={t['aria-label']['previousSlide']} disabled={isFirstSlide}>
                <Arrow icon={ChevronLeft} width={44} height={44} />
              </button>

              <H4 as="span">
                {activeSlide}/{gallery.length}
              </H4>
              <button onClick={handleNext} title={t['aria-label']['nextSlide']} disabled={isLastSlide}>
                <Arrow icon={ChevronRight} width={44} height={44} />
              </button>
            </Controls>
          </GalleryWrapper>
        </ProjectContainer>
      )}
    </i18nContext.Consumer>
  );
};

const VideoURLContainer = styled.div`
  padding-top: clamp(1.94rem, 1.6167rem + 1.437vw, 2.91rem);
`;

const FeatureList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  ${breakpointUpTo.lg`
    grid-template-columns: repeat(1, 1fr);
    gap: 0;
  `}
  gap: 1.06rem;

  > div {
    ${breakpointUpTo.lg`
    &:last-child {
    border-top: 0;
    }
  `}
    border-top: 1.5px solid ${({ theme }) => theme.color.teal.dark};
    &:nth-child(1) {
      > div {
        &:nth-child(even) {
          margin-bottom: -1.5px;
        }
      }
    }

    > div {
      border-bottom: 1.5px solid ${({ theme }) => theme.color.teal.dark};
      span:first-child {
        margin-top: clamp(0.94rem, 0.8167rem + 0.5481vw, 1.31rem);
      }
      span:last-child {
        margin-block: clamp(0.94rem, 0.8167rem + 0.5481vw, 1.31rem);
      }

      justify-content: space-between;
      display: flex;
      flex-wrap: wrap;

      .title {
        color: ${({ theme }) => theme.color.teal.dark};
        font-size: 1.125rem;
        font-style: normal;
        font-weight: 600;
        line-height: normal;
        letter-spacing: 0.0225rem;
      }
    }
  }
`;

const InformationsWrapper = styled(ContainerRightSide)`
  --paddingBreak: ${({ theme }) => theme.container.defaultPadding};

  ${breakpointUpTo.md`
  width: min(1440px, 100% - var(--paddingBreak));
  margin-inline: auto;
  grid-row:2;
  `}
`;
const GalleryWrapper = styled(ContainerLeftSide)`
  ${breakpointUpTo.md`
    width: 100%;
  `}
`;

const DescriptionWrapper = styled.div`
  p {
    color: ${({ theme }) => theme.color.teal.dark};
    padding-bottom: clamp(1.94rem, 1.46rem + 2.1333vw, 3.38rem);
  }
`;

const TitleWrapper = styled.div`
  h3,
  p {
    color: ${({ theme }) => theme.color.teal.light};
  }
  padding-bottom: clamp(1.06rem, 0.6rem + 2.0444vw, 2.44rem);
`;

const ProjectContainer = styled.div`
  width: 100%;
  margin: auto;
  max-width: 1440px;
  position: relative;
  padding-inline: 0.9375rem;
  ${breakpointUpTo.md`
    padding-inline: 0;
  `}
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: clamp(1.88rem, 1.1333rem + 3.3185vw, 4.19rem);
  flex-wrap: wrap;
  padding-bottom: clamp(1.88rem, 1.1333rem + 3.3185vw, 4.12rem);

  ${breakpointUpTo.md`
    grid-template-columns: repeat(1, 100%);
  `}
`;

const Arrow = styled(Icon)`
  mask-size: 200%;
  mask-position: center;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
  ${breakpointUpTo.md`
    display: none;
  `}

  span {
    color: ${({ theme }) => theme.color.teal.light};
  }
  button {
    display: grid;
    place-content: center;
    border-radius: 50%;
    cursor: pointer;
    border: none;
    background-color: ${({ theme }) => theme.color.white};
  }
`;

const ImageWrapper = styled.div`
  max-width: clamp(20rem, -8.5714rem + 59.5238vw, 45rem);
  height: clamp(25rem, 20.4286rem + 9.5238vw, 29rem);
  ${breakpointUpTo.md`
    max-width:100%;
  height: 16rem;
  `}
  margin-inline: auto;
`;

const Chip = styled(P)`
  ${breakpointFrom.xl`
  text-align: center;
  `}
  display: block;
  max-width: max-content;
  color: ${({ theme }) => theme.color.teal.dark};
  background: ${({ theme }) => theme.color.yellow};
  font-weight: 400;
  padding-inline: 1rem;
  border-radius: 1.25rem;
  text-transform: capitalize;
`;

type Props = {
  project: ProjectCard;
};
